import { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Sparkles, Calendar, Star, Search } from "lucide-react";
import { CONSULTATION_URL } from "@/data/constants";
import { trackConversion } from "@/hooks/useSiteAnalytics";
import useSEO, { caseStudySchema, breadcrumbSchema } from "@/hooks/useSEO";

const CATEGORY_LABELS = {
  water: "Purified & Alkaline Water",
  alkaline: "Alkaline Water",
  purified: "Purified Water",
  sparkling: "Sparkling Water",
  functional: "Functional Beverages",
  energy: "Energy Drinks",
  coffee: "Coffee",
  tea: "Tea",
  juice: "Juice",
  protein: "Protein Beverages",
  supplements: "Supplements",
  other: "Other",
};

// -----------------------------------------------------------------
//  Attribution helper — stored in localStorage so it survives page nav
// -----------------------------------------------------------------
export const setAttribution = (data) => {
  try {
    const existing = JSON.parse(localStorage.getItem("flowtalis_attribution") || "{}");
    const first_touch = existing.first_touch_brand || data.brand;
    const payload = {
      first_touch_brand: first_touch,
      last_touch_brand: data.brand,
      source_page: data.source_page,
      cta_clicked: data.cta_clicked,
      updated_at: new Date().toISOString(),
    };
    localStorage.setItem("flowtalis_attribution", JSON.stringify(payload));
  } catch (_e) { /* localStorage may be unavailable — safe to ignore */ }
};

export const getAttribution = () => {
  try { return JSON.parse(localStorage.getItem("flowtalis_attribution") || "{}"); }
  catch (_e) { return {}; }
};

const BrandThumb = ({ item }) => {
  // Cinematic hero: silent 6-second loop takes precedence when uploaded
  if (item.hero_video_url) {
    return (
      <div className="aspect-[4/5] rounded-xl overflow-hidden bg-black relative" data-testid={`hero-video-${item.slug}`}>
        <video
          src={item.hero_video_url}
          poster={item.bottle_image_url || item.hero_image_url || undefined}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-label={item.alt_text || `${item.brand_name} hero video`}
          className="w-full h-full object-cover"
        />
      </div>
    );
  }
  const src = item.bottle_image_url || item.hero_image_url;
  if (src) {
    return (
      <div className="aspect-[4/5] rounded-xl overflow-hidden bg-[var(--ft-light-gray)] flex items-center justify-center p-4">
        <img src={src} alt={item.alt_text || item.brand_name} className="w-full h-full object-contain" loading="lazy" />
      </div>
    );
  }
  // Fallback: gold circle emblem with brand initials
  const initials = item.brand_name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div className="aspect-[4/5] rounded-xl bg-gradient-to-br from-[#fafafa] to-[#eeeeee] flex items-center justify-center">
      <div className="w-24 h-24 rounded-full border-2 border-[var(--ft-gold)] flex items-center justify-center">
        <span className="text-2xl font-bold" style={{ color: "var(--ft-gold)" }}>{initials}</span>
      </div>
    </div>
  );
};

// =================================================================
//  Portfolio index — searchable + filterable
// =================================================================
export const Portfolio = () => {
  useSEO({
    title: "Portfolio — Every FLOWTALIS™ Brand Launched",
    description: "Explore the beverage brands FLOWTALIS™ has helped launch — the challenge, solution, and services delivered on each engagement. Purified water, alkaline, functional wellness, and more.",
    canonical: "https://flowtalis.com/portfolio",
    jsonLd: breadcrumbSchema([
      { label: "Home", href: "/" },
      { label: "Portfolio", href: "/portfolio" },
    ]),
  });
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const loadShowcase = () => {
    setLoading(true);
    setLoadError(false);
    Promise.allSettled([
      api.get("/showcase").then((r) => setItems(r.data.showcase || [])),
      api.get("/showcase/categories").then((r) => setCategories(r.data.categories || [])),
    ]).then((results) => {
      // If both requests failed, surface a retry affordance
      if (results.every((r) => r.status === "rejected")) setLoadError(true);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { loadShowcase(); }, []);

  const filtered = useMemo(() => {
    return items.filter((i) => {
      if (cat && i.beverage_category !== cat) return false;
      if (q && !`${i.brand_name} ${i.product_name} ${i.description}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [items, cat, q]);

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="bg-white py-20 border-b border-[var(--ft-border)]">
        <div className="ft-container max-w-4xl ft-fade-in">
          <div className="ft-eyebrow mb-5">Portfolio</div>
          <span className="ft-gold-rule-lg" />
          <h1 className="mt-6 text-[clamp(2.25rem,4.5vw,3.75rem)] font-bold leading-[1.02] tracking-tight text-[var(--ft-black)]">
            Every FLOWTALIS <span className="ft-serif font-medium" style={{ color: "var(--ft-gold)" }}>brand launched</span>.
          </h1>
          <p className="mt-8 text-[var(--ft-charcoal)] text-lg leading-relaxed max-w-2xl">
            Explore the beverage brands we&apos;ve helped launch — with the challenge, the solution, and the services delivered on each engagement.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-10 bg-white sticky top-[112px] z-30 border-b border-[var(--ft-border)]">
        <div className="ft-container flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ft-muted)]" />
            <input
              className="ft-input pl-9"
              placeholder="Search brands, products…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="portfolio-search"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setCat("")} data-testid="portfolio-filter-all"
                    className={`fx-chip ${cat === "" ? "is-active" : ""}`}>
              All
            </button>
            {categories.map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`portfolio-filter-${c}`}
                className={`fx-chip capitalize ${cat === c ? "is-active" : ""}`}>
                {CATEGORY_LABELS[c] || c}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="py-16 bg-white">
        <div className="ft-container">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="portfolio-loading">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="ft-card animate-pulse">
                  <div className="aspect-[4/5] rounded-xl bg-[var(--ft-light-gray)]" />
                  <div className="mt-5 h-4 w-24 bg-[var(--ft-light-gray)] rounded-full" />
                  <div className="mt-3 h-5 w-3/4 bg-[var(--ft-light-gray)] rounded" />
                  <div className="mt-2 h-3 w-1/2 bg-[var(--ft-light-gray)] rounded" />
                  <div className="mt-4 h-3 w-full bg-[var(--ft-light-gray)] rounded" />
                  <div className="mt-2 h-3 w-5/6 bg-[var(--ft-light-gray)] rounded" />
                </div>
              ))}
            </div>
          ) : loadError && items.length === 0 ? (
            <div className="text-center py-20 max-w-md mx-auto" data-testid="portfolio-error">
              <div className="text-[var(--ft-charcoal)] font-semibold">We couldn&apos;t load the portfolio right now.</div>
              <p className="mt-2 text-sm text-[var(--ft-muted)]">Check your connection or try again in a moment.</p>
              <button onClick={loadShowcase} className="ft-btn-primary mt-6" data-testid="portfolio-retry">Retry</button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="fx-empty max-w-lg mx-auto" data-testid="portfolio-empty">
              <div className="fx-empty-icon"><Sparkles size={20}/></div>
              <h4>No brands match your filters yet</h4>
              <p>Try broadening your search or clearing the category filter to see the full portfolio.</p>
              {(cat || q) && (
                <button onClick={() => { setCat(""); setQ(""); }} className="ft-btn-ghost mt-2" data-testid="portfolio-clear-filters">Clear filters →</button>
              )}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="portfolio-grid">
              {filtered.map((item) => (
                <Link
                  key={item.id}
                  to={`/portfolio/${item.slug}`}
                  className="ft-card block group"
                  data-testid={`portfolio-card-${item.slug}`}
                  onClick={() => {
                    setAttribution({ brand: item.brand_name, source_page: "/portfolio", cta_clicked: "portfolio_card" });
                    api.post(`/showcase/${item.id}/event`, { event_type: "click" }).catch(() => {});
                  }}
                >
                  <div className="relative">
                    <BrandThumb item={item} />
                    {item.is_concept ? (
                      <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[0.6rem] font-bold uppercase tracking-[0.22em] px-2.5 py-1 rounded-full bg-[var(--ft-black)] text-[var(--ft-gold)] border border-[var(--ft-gold)]" data-testid={`concept-badge-${item.slug}`}>
                        <Sparkles size={10} /> Design Concept
                      </span>
                    ) : item.featured && (
                      <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[0.65rem] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-[var(--ft-gold)] text-white">
                        <Star size={10} fill="white" /> Featured
                      </span>
                    )}
                  </div>
                  <div className="mt-5 flex items-center gap-2 flex-wrap">
                    {item.industry && <span className="ft-badge">{item.industry}</span>}
                  </div>
                  <h2 className="mt-3 text-xl font-semibold text-[var(--ft-black)]">{item.brand_name}</h2>
                  <p className="mt-1 text-sm text-[var(--ft-muted)]">{item.product_name}</p>
                  <p className="mt-3 text-sm text-[var(--ft-charcoal)] leading-relaxed line-clamp-3">{item.description}</p>
                  {item.services?.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {item.services.slice(0, 3).map((s) => (
                        <span key={s} className="text-[0.65rem] uppercase tracking-wider font-semibold text-[var(--ft-muted)] px-2 py-0.5 rounded border border-[var(--ft-border)]">{s}</span>
                      ))}
                    </div>
                  )}
                  <div className="mt-5 pt-4 border-t border-[var(--ft-border)] inline-flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--ft-gold)" }}>
                    View Case Study <ArrowRight size={14} />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
};

// =================================================================
//  Case Study — /portfolio/:slug
// =================================================================
export const CaseStudy = () => {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useSEO({
    title: item ? `${item.brand_name} — Case Study by FLOWTALIS™` : "Case Study — FLOWTALIS™",
    description: item?.description || "A FLOWTALIS™ private-label beverage case study — from concept to shipped product.",
    canonical: `https://flowtalis.com/portfolio/${slug}`,
    image: item?.hero_image_url || undefined,
    type: "article",
    jsonLd: item ? [
      caseStudySchema({
        name: item.brand_name,
        slug,
        description: item.description,
        image: item.hero_image_url,
        industry: item.industry,
      }),
      breadcrumbSchema([
        { label: "Home", href: "/" },
        { label: "Portfolio", href: "/portfolio" },
        { label: item.brand_name, href: `/portfolio/${slug}` },
      ]),
    ] : undefined,
  });

  useEffect(() => {
    api.get(`/showcase/${slug}`).then((r) => {
      setItem(r.data);
      api.post(`/showcase/${r.data.id}/event`, { event_type: "view" }).catch(() => {});
      trackConversion("portfolio_view", {
        portfolio_slug: slug,
        brand_name: r.data?.brand_name || undefined,
        product_category: r.data?.beverage_category || undefined,
        is_concept: !!r.data?.is_concept,
      });
    }).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <PublicLayout><div className="ft-container py-32 text-center">
      <h1 className="text-3xl font-bold">Case study not found</h1>
      <Link to="/portfolio" className="ft-btn-gold mt-6 inline-flex">Back to Portfolio</Link>
    </div></PublicLayout>
  );

  if (!item) return <PublicLayout><div className="ft-container py-32">Loading…</div></PublicLayout>;

  const handleCta = () => {
    setAttribution({ brand: item.brand_name, source_page: `/portfolio/${slug}`, cta_clicked: "launch_your_brand" });
    api.post(`/showcase/${item.id}/event`, { event_type: "cta_click" }).catch(() => {});
  };

  return (
    <PublicLayout>
      <section className="bg-white py-16 border-b border-[var(--ft-border)]">
        <div className="ft-container ft-fade-in">
          <Link to="/portfolio" className="inline-flex items-center gap-2 text-[var(--ft-muted)] hover:text-[var(--ft-gold)] mb-6 text-sm" data-testid="case-back">
            <ArrowLeft size={16} /> Back to Portfolio
          </Link>
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <div className="flex items-center gap-2 flex-wrap mb-4">
                {item.is_concept && (
                  <span className="inline-flex items-center gap-1 text-[0.6rem] font-bold uppercase tracking-[0.22em] px-2.5 py-1 rounded-full bg-[var(--ft-black)] text-[var(--ft-gold)] border border-[var(--ft-gold)]" data-testid={`concept-badge-${item.slug}`}>
                    <Sparkles size={10} /> Design Concept
                  </span>
                )}
                {!item.is_concept && item.industry && <span className="ft-badge">{item.industry}</span>}
                <span className="text-[0.65rem] uppercase tracking-[0.24em] text-[var(--ft-muted)] font-semibold">
                  {CATEGORY_LABELS[item.beverage_category] || item.beverage_category}
                </span>
              </div>
              <div className="ft-eyebrow mb-3">{item.is_concept ? "FLOWTALIS Design Concept" : "Case Study"}</div>
              <span className="ft-gold-rule-lg" />
              <h1 className="mt-6 text-[clamp(2rem,4vw,3.25rem)] font-bold leading-[1.05] tracking-tight text-[var(--ft-black)]">
                {item.brand_name}
              </h1>
              <p className="mt-3 text-[var(--ft-muted)]">{item.product_name}{item.client_name ? ` · ${item.client_name}` : ""}</p>
              {item.is_concept && (
                <div className="mt-4 rounded-lg border border-[var(--ft-gold)]/40 bg-[var(--ft-gold)]/8 px-4 py-3 text-[13px] leading-relaxed text-[var(--ft-charcoal)]" data-testid="case-concept-disclaimer">
                  This is a FLOWTALIS-developed design concept created to demonstrate our capabilities. It is not a client engagement or endorsement.
                </div>
              )}
              <p className="mt-6 text-[var(--ft-charcoal)] text-[15px] leading-relaxed">{item.description}</p>
            </div>
            <div>
              <BrandThumb item={item} />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="ft-container max-w-4xl space-y-12">
          {item.story && (
            <div>
              <div className="ft-eyebrow mb-3">Brand Overview</div>
              <p className="text-[15px] leading-relaxed text-[var(--ft-charcoal)]">{item.story}</p>
            </div>
          )}
          {item.challenge && (
            <div>
              <div className="ft-eyebrow mb-3">Client Challenge</div>
              <p className="text-[15px] leading-relaxed text-[var(--ft-charcoal)]">{item.challenge}</p>
            </div>
          )}
          {item.solution && (
            <div>
              <div className="ft-eyebrow mb-3">FLOWTALIS Solution</div>
              <p className="text-[15px] leading-relaxed text-[var(--ft-charcoal)]">{item.solution}</p>
            </div>
          )}
          {item.services?.length > 0 && (
            <div>
              <div className="ft-eyebrow mb-3">Services Provided</div>
              <div className="flex flex-wrap gap-2">
                {item.services.map((s) => (
                  <span key={s} className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-[var(--ft-border)]">
                    <Sparkles size={11} style={{ color: "var(--ft-gold)" }} /> {s}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="grid md:grid-cols-3 gap-4 pt-6">
            <div className="ft-card-flat">
              <div className="ft-eyebrow mb-1">Category</div>
              <div className="font-semibold">{CATEGORY_LABELS[item.beverage_category] || item.beverage_category}</div>
            </div>
            <div className="ft-card-flat">
              <div className="ft-eyebrow mb-1">Volume</div>
              <div className="font-semibold">{item.volume}</div>
            </div>
            {item.is_concept ? (
              <div className="ft-card-flat">
                <div className="ft-eyebrow mb-1 flex items-center gap-1"><Calendar size={11}/> Status</div>
                <div className="font-semibold" data-testid="case-status">In Development</div>
              </div>
            ) : item.launch_date && (
              <div className="ft-card-flat">
                <div className="ft-eyebrow mb-1 flex items-center gap-1"><Calendar size={11}/> Launched</div>
                <div className="font-semibold">{new Date(item.launch_date).toLocaleDateString("en-US", { month: "long", year: "numeric" })}</div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="py-24 bg-[var(--ft-light-gray)] text-center">
        <div className="ft-container max-w-2xl">
          <div className="ft-eyebrow mb-5">Ready to Launch</div>
          <span className="ft-gold-rule-lg mx-auto" />
          <h2 className="mt-6 text-[clamp(1.875rem,3.75vw,3rem)] font-bold leading-[1.05] tracking-tight text-[var(--ft-black)]">
            Launch your <span className="ft-serif font-medium" style={{ color: "var(--ft-gold)" }}>beverage brand</span>.
          </h2>
          <p className="text-[var(--ft-muted)] mt-6 mb-8 text-lg">
            Every FLOWTALIS launch starts with a scoping consultation.
          </p>
          <a href={CONSULTATION_URL} target="_blank" rel="noopener noreferrer" onClick={handleCta} className="ft-btn-gold" data-testid="case-cta">
            Launch Your Brand <ArrowRight size={16} />
          </a>
        </div>
      </section>
    </PublicLayout>
  );
};
